// Package imports:
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf} from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import LmdTable from '../../ui-elements/Table/LmdTable';
import Fyrirvari from '../Fyrirvari/Fyrirvari';
import Label from '../../ui-elements/Label/Label';
// Service imports:
import { getTableDate } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdAvailableReports } from '../../types/HlutabrefTypes';

interface IOwnProps {
    symbol: string,
    limit?: number
}

const getPeriodName = (pn: string | null) => {
    switch (pn) {
        case '3M':
            return '3 mán.';
        case '6M':
            return '6 mán.';
        case '9M':
            return '9 mán.';
        case 'Y':
            return 'Ár';
        default:
            return null;
    }
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const CompanyReportsFiles: React.FC<Props> = ({
    symbol,
    limit = 10,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [apiData] = useApiLmdData<IApiLmdAvailableReports[]>(
        `/v1/market_data/v1/questor/available_reports/symbol/${symbol}/periods/[3M;6M;9M;Y]/`,
        accessToken,
        refreshRateMs
    );
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);
    const openDisclaimer = () => setDisclaimerOpen(true);
    const closeDisclaimer = () => setDisclaimerOpen(false);

    const handleData = () => {
        const { data, error } = apiData;
        if (data === null) {
            return apiData;
        } else {
            const newData = data.slice(-limit);
            newData.reverse();
            return {
                error,
                data: newData
            }
        }
    }

    return (
        <DisplayBox
            className='KCL_company-reports-files'
            title="Árs- og -árshlutareikningar"
            {...props}
        >
            <LmdTable
                apiData={handleData()}
                columns={[{
                    title: 'Ár',
                    dataAccessor: ({FiscalYear}) => FiscalYear,
                    textAlign: 'left'
                }, {
                    title: 'Tímabil',
                    dataAccessor: ({PeriodName}) => getPeriodName(PeriodName),
                    textAlign: 'left'
                }, {
                    title: 'Frá',
                    dataAccessor: ({PeriodStart}) => getTableDate(PeriodStart, 'DD/MM/YYYY', '.'),
                    textAlign: 'left'
                }, {
                    title: 'Til',
                    dataAccessor: ({PeriodEnd}) => getTableDate(PeriodEnd, 'DD/MM/YYYY', '.'),
                    textAlign: 'left'
                }, {
                    title: '',
                    dataAccessor: ({Ticker, PeriodName, FiscalYear}) => (
                        <a href={`https://questor-reports.s3-eu-west-1.amazonaws.com/${Ticker}-${PeriodName}-${FiscalYear}.pdf`} target="_blank" rel="noreferrer">
                            <FontAwesomeIcon icon={faFilePdf} className="icon-fa" />
                        </a>
                    ),
                    textAlign: 'center'
                }]}
                tableSize='lg'
            />
            <span className="label-wrapper" onClick={openDisclaimer}>
                <Label text="Fyrirvari" labelType="disclaimer" />
            </span>
            <Fyrirvari show={disclaimerOpen} close={closeDisclaimer}>
                <p>Keldan ábyrgist ekki áreiðanleika eða réttmæti upplýsinga sem settar eru fram.</p>
                <p>Árs- og árshlutareikningum er safnað saman af vefsíðum fyrirtækjanna eða úr kauphallartilkynningum, ekki er víst að um réttar upplýsingar sé að ræða.</p>
            </Fyrirvari>
        </DisplayBox>
    );
}

export default CompanyReportsFiles;